import React, { useCallback, useContext, useState } from 'react';
import { ChatbotWidgetContext } from 'components/ChatbotWidget/utils/ChatbotWidgetContext';
import { Input, Icon } from 'gd-design-library';
import { Button } from 'components/baseComponents/Button';
import styles from './ChatInputs.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);
const CN = 'chat-inputs';
const textFieldClass = `${CN}__textfield`;
const fileButtonClass = `${CN}__file-button`;

interface ChatInputsProps {
  isFirstUserMessage: boolean;
  disabled: boolean;
}

export const ChatInputs = ({ isFirstUserMessage, disabled }: ChatInputsProps): React.ReactElement => {
  const { client } = useContext(ChatbotWidgetContext);
  const [ input, setInput ] = useState('');
  const [ isSendingFile, setIsSendingFile ] = useState(false);

  const sendTextMessage = useCallback(() => {
    if (!input.trim() || !client) return;

    client.sendTextMessage(input);
    setInput('');
  }, [ client, input, isFirstUserMessage ]);

  const sendFileMessage = useCallback(() => {
    if (!client) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e): Promise<void> => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setIsSendingFile(true);
        await client.sendFileMessage(file);
        setIsSendingFile(false);
      }
    };
    input.click();
  }, [ client ]);

  return (
    <div className={ cx(CN) }>
      <Button
        isIcon
        variant='text'
        onClick={ sendFileMessage }
        disabled={ disabled }
        loading={ isSendingFile }
        className={ cx(fileButtonClass) }
      >
        <Icon name='attachment' width={ 24 } height={ 24 } />
      </Button>
      <Input
        variant='text'
        name='message'
        value={ input }
        onChange={ (e): void => setInput(e.target.value) }
        onKeyUp={ (e): void => e.key === 'Enter' && sendTextMessage() }
        className={ cx(textFieldClass) }
        autocomplete='off'
      />
      <Button variant='primary' onClick={ sendTextMessage } disabled={ disabled }>Send</Button>
    </div>
  );
};
